import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import {
  AppShell as MantineAppShell,
  Burger,
  Group,
  ScrollArea,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { useSession } from "@prostocheck/data-web";
import { cn } from "@prostocheck/shared";
import { Button, Logo } from "@prostocheck/ui";

import { ExtensionGateBadge } from "../extension-gate-badge";
import { LanguageSwitcher } from "../language-switcher";
import {
  HEADER_HEIGHT,
  NAVBAR_BREAKPOINT,
  NAVBAR_WIDTH,
  SIDEBAR_NAV_ITEMS,
} from "./app-shell.constants";

const navLinkClass = ({ isActive }: { isActive: boolean }, indent = false) =>
  cn(
    "block rounded-lg py-2 text-sm font-medium transition-colors",
    indent ? "ml-3 px-3" : "px-3",
    isActive
      ? "bg-brand-50 text-brand-700"
      : "text-ink-700 hover:bg-surface-muted hover:text-ink-900",
  );

export const AppShell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSidebarOpen, sidebar] = useDisclosure(false);

  const { session, endSession } = useSession();
  const registers = session?.registers ?? [];

  const handleSignOut = () => {
    endSession();
    navigate("/login", { replace: true });
  };

  return (
    <MantineAppShell
      header={{ height: HEADER_HEIGHT }}
      navbar={{
        width: NAVBAR_WIDTH,
        breakpoint: NAVBAR_BREAKPOINT,
        collapsed: { mobile: !isSidebarOpen },
      }}
      padding="lg"
    >
      <MantineAppShell.Header>
        <Group h="100%" px="md" gap="md" wrap="nowrap">
          <Burger
            size="sm"
            hiddenFrom={NAVBAR_BREAKPOINT}
            opened={isSidebarOpen}
            onClick={sidebar.toggle}
            aria-label={
              isSidebarOpen ? t("shell.closeMenu") : t("shell.openMenu")
            }
          />

          <Logo />

          <Group ml="auto" gap="sm" wrap="nowrap">
            <Text
              size="sm"
              c="dimmed"
              lineClamp={1}
              visibleFrom="sm"
              maw={420}
              className="min-w-0"
              title={session?.owner.name}
            >
              {session?.owner.name}
            </Text>
            <ExtensionGateBadge />
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              {t("shell.signOut")}
            </Button>
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="md">
        <MantineAppShell.Section grow component={ScrollArea}>
          <nav aria-label={t("shell.navAriaLabel")}>
            <ul className="space-y-1">
              {SIDEBAR_NAV_ITEMS.map(item => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={sidebar.close}
                    className={navLinkClass}
                  >
                    {t(item.labelKey)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {registers.length > 0 && (
            <div className="mt-6 border-t border-ink-900/8 pt-5">
              <Text
                size="xs"
                fw={600}
                c="dimmed"
                tt="uppercase"
                mb="xs"
                px="sm"
              >
                {t("shell.myRegisters")}
              </Text>

              <ul className="space-y-1">
                {registers.map(register => (
                  <li key={register.fiscalNumber}>
                    <NavLink
                      to={`/registers/${register.fiscalNumber}`}
                      onClick={sidebar.close}
                      className={state => navLinkClass(state, true)}
                    >
                      <span className="block truncate">{register.name}</span>
                      <span className="block truncate text-xs text-ink-500">
                        {t("shell.registerFiscalNumber", {
                          fiscalNumber: register.fiscalNumber,
                        })}
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </MantineAppShell.Section>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>
        <Outlet />

        {/* Version · short commit · commit date, stamped in at build time. */}
        <Text
          component="p"
          size="xs"
          c="dimmed"
          ta="center"
          mt="xl"
          className="tabular-nums"
        >
          {__BUILD_INFO__}
        </Text>
      </MantineAppShell.Main>
    </MantineAppShell>
  );
};
